import React from 'react';
import { CampaignRune } from '../primitives/CampaignRune';
import { CalmPanel } from './CalmPanel';
import { NextSessionPip } from './NextSessionPip';

export const CalmCampaignHero = ({ campaign, extra, palette, bodyFont, headingFont, c, theme, onEnterAsDm, onOpenSettings, onCreateInvite }) => {
  const isDemoCampaign = !!campaign?.isDemo;
  const canEnterAsDm = campaign?.role === 'owner' || campaign?.role === 'dm';

  return (
  <div style={{ marginBottom: 22 }}>
    <CalmPanel theme={theme} padding="20px 22px">
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 18,
        alignItems: 'flex-start',
      }}>
        {/* Campaign rune — stands on its own without a tile wrapper.
            Palette identity for this hero is carried by the campaign name
            color, status pip, and quest-line accent. */}
        <CampaignRune
          name={campaign.rune}
          size={48}
          color={palette.light}
          glow={1}
          shadow={1}
          style={{ flex: '0 0 48px' }}
        />

        {/* Identity + blurb */}
        <div style={{ minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'baseline',
            gap: 10, flexWrap: 'wrap',
            marginBottom: 4,
          }}>
            <h1 style={{
              margin: 0,
              fontFamily: headingFont,
              fontSize: '1.7rem',
              fontWeight: 600,
              color: palette.light,
              letterSpacing: '-0.005em',
              lineHeight: 1.15,
            }}>{campaign.name}</h1>
            <NextSessionPip schedule={campaign.schedule} palette={palette} c={c} bodyFont={bodyFont} />
          </div>
          {(campaign.dm || campaign.players > 0 || campaign.schedule) && (
            <div style={{
              fontFamily: bodyFont,
              fontSize: '0.86rem',
              color: c.textSecondary,
              marginBottom: 10,
            }}>
              {campaign.dm && (
                <>Run by <span style={{ color: c.textPrimary, fontWeight: 600 }}>{campaign.dm}</span></>
              )}
              {(campaign.players > 0 || campaign.schedule) && (
                <span style={{ color: c.textMuted }}>
                  {campaign.dm ? ' · ' : ''}
                  {campaign.players > 0 ? `${campaign.players} at the table` : ''}
                  {campaign.players > 0 && campaign.schedule ? ' · ' : ''}
                  {campaign.schedule || ''}
                </span>
              )}
            </div>
          )}
          {campaign.blurb && (
            <p style={{
              margin: 0,
              fontFamily: bodyFont,
              fontSize: '0.92rem',
              color: c.textSecondary,
              lineHeight: 1.55,
              fontStyle: 'italic',
              opacity: 0.85,
              maxWidth: 640,
            }}>{campaign.blurb}</p>
          )}
          {campaign.description && (
            <p style={{
              margin: '6px 0 0',
              fontFamily: bodyFont,
              fontSize: '0.86rem',
              color: c.textSecondary,
              lineHeight: 1.55,
              opacity: 0.78,
              maxWidth: 640,
              whiteSpace: 'pre-wrap',
            }}>{campaign.description}</p>
          )}
          {extra.quest && (
            <div style={{
              marginTop: 10,
              fontFamily: bodyFont,
              fontSize: '0.78rem',
              color: c.textMuted,
            }}>
              Current quest: <span style={{ color: palette.light }}>{extra.quest}</span>
            </div>
          )}
        </div>

        {/* Right meta column — day number + invite */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 6,
          flex: '0 0 auto',
        }}>
          <span style={{
            fontFamily: bodyFont,
            fontSize: '0.7rem',
            color: c.textMuted,
            letterSpacing: '0.02em',
          }}>Day {extra.day || 1}</span>
          {!isDemoCampaign && extra.invite && (
            <code style={{
              fontFamily: bodyFont,
              fontSize: '0.72rem',
              color: c.textSecondary,
              padding: '3px 8px',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 4,
              letterSpacing: '0.02em',
            }}>{extra.invite || '—'}</code>
          )}
          {canEnterAsDm && (
            <div style={{ marginTop: 2, display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
              {!isDemoCampaign && onCreateInvite && (
                <button
                  type="button"
                  onClick={onCreateInvite}
                  style={{
                    padding: '5px 10px',
                    background: 'transparent',
                    border: `1px solid ${c.borderLight}`,
                    borderRadius: 4,
                    color: c.textSecondary,
                    fontFamily: bodyFont,
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                  }}
                >Create invite</button>
              )}
              <button
                type="button"
                onClick={onEnterAsDm}
                style={{
                  padding: '6px 12px',
                  background: palette.color,
                  border: `1px solid ${palette.color}`,
                  borderRadius: 4,
                  color: c.textOnAccent,
                  fontFamily: bodyFont,
                  fontSize: '0.76rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >👑 Enter as DM</button>
              {!isDemoCampaign && onOpenSettings && (
                <button
                  type="button"
                  onClick={onOpenSettings}
                  style={{
                    padding: '5px 10px',
                    background: 'transparent',
                    border: `1px solid ${c.borderLight}`,
                    borderRadius: 4,
                    color: c.textSecondary,
                    fontFamily: bodyFont,
                    fontSize: '0.72rem',
                    cursor: 'pointer',
                  }}
                >⚙ Settings</button>
              )}
            </div>
          )}
        </div>
      </div>
    </CalmPanel>
  </div>
  );
};
